import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProviderType } from "../common/ObjectTypes";
import webhook from "../../images/webhook.svg";
import api from "../../images/api.svg";
import { useKeycloak } from "@react-keycloak/web";
import { loadProviderTypes } from '../../api/connections/loadProviderTypesNewConnection';
import { textClasses, buttonClasses } from "../common/tailwindClassnames";

type WebhookApiPushModalProps = {
    providerTypes: ProviderType[];
    selectedType: string | null;
    onSelectProviderType: Function;
    setSelectedType: Function;
    setShowModal?: Function;
    isModal?: boolean;
    showModal?: boolean;
};

const WebhookApiPushModal: React.FC<WebhookApiPushModalProps> = ({
    providerTypes,
    selectedType,
    onSelectProviderType,
    setSelectedType,
    setShowModal,
    isModal = false,
    showModal = false
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const [specialProviderTypes, setSpecialProviderTypes] = useState<{ [key: string]: ProviderType }>({});

    // Helper function to check if a specific provider type is selected
    const isProviderSelected = (providerValue: string): boolean => {
        return !!(
            // Check in regular provider types
            providerTypes.find(pt => pt.value === providerValue && pt.id === selectedType) ||
            // Check in special provider types
            (specialProviderTypes[providerValue] &&
                specialProviderTypes[providerValue].id === selectedType)
        );
    };

    // Load special provider types (webhook and API push) on component mount
    useEffect(() => {
        const loadSpecialProviderTypes = async () => {
            if (keycloak && keycloak.token) {
                // Search specifically for generic_webhooks and api_push
                const searchTerms = ["webhook", "api"];

                try {
                    // Make all API calls in parallel using Promise.all
                    const searchResults = await Promise.all(
                        searchTerms.map(term =>
                            loadProviderTypes(
                                keycloak.token as string,
                                0,
                                term,
                                [],
                                false
                            )
                        )
                    );

                    // Combine all results from the parallel calls
                    const allProviderTypes = searchResults.flatMap(result => result[0]);

                    // Extract and store the specific provider types we need
                    const specialTypes: { [key: string]: ProviderType } = {};

                    allProviderTypes.forEach(provider => {
                        if (provider.value === "generic_webhooks" || provider.value === "api_push") {
                            specialTypes[provider.value] = provider;
                        }
                    });

                    // Update state once with all found provider types
                    if (Object.keys(specialTypes).length > 0) {
                        setSpecialProviderTypes(specialTypes);
                    }
                } catch (error) {
                    console.error("Error loading special provider types:", error);
                }
            }
        };

        loadSpecialProviderTypes();
    }, [keycloak]);

    // Helper function to handle provider type selection
    const handleSelectProviderType = (providerValue: string) => {
        const navigateToNextScreen = (provider: ProviderType) => {
            if (isModal && setShowModal) {
                setShowModal(false);
            }
            navigate(`/connections/new/configureprovider/${provider.value}`);
        };

        if (specialProviderTypes[providerValue]) {
            const provider = specialProviderTypes[providerValue];
            onSelectProviderType(provider);
            setSelectedType(provider.id);

            navigateToNextScreen(provider);
            return;
        }

        const provider = providerTypes.find(pt => pt.value === providerValue);
        if (provider) {
            onSelectProviderType(provider);
            setSelectedType(provider.id);

            navigateToNextScreen(provider);
        } else {
            const searchForProviderType = async () => {
                if (keycloak && keycloak.token) {
                    try {
                        const [providerTypeResults] = await loadProviderTypes(
                            keycloak.token as string,
                            0,
                            providerValue,
                            [],
                            false
                        );

                        const foundProvider = providerTypeResults.find(pt => pt.value === providerValue);
                        if (foundProvider) {
                            onSelectProviderType(foundProvider);
                            setSelectedType(foundProvider.id);

                            // Cache this provider type for future use
                            setSpecialProviderTypes(prev => ({
                                ...prev,
                                [providerValue]: foundProvider
                            }));

                            navigateToNextScreen(foundProvider);
                        } else {
                            console.warn(`Provider type '${providerValue}' not found in API results`);
                        }
                    } catch (error) {
                        console.error(`Error searching for provider type '${providerValue}':`, error);
                    }
                }
            };

            searchForProviderType();
        }
    };

    // The content that's shared between both views
    const renderContent = () => (
        <>
            <p className={textClasses.subheading + " mb-4"}>Can't find the data source you are looking for?</p>
            <p className="text-off-black dark:text-white transition-colors duration-300">Consider using a webhook or API Push {!isModal &&
                <a
                    href="https://support.earthranger.com/connections/create-connection#step-1-select-a-data-provider-0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-status-green dark:text-dark-green-100 underline cursor-pointer transition-colors duration-300"
                >
                    Learn more
                </a>}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8 items-stretch my-4 w-full">
                {/* Add dark-green border when webhook provider is selected */}
                <div
                    className={`flex flex-col w-full sm:w-1/2 items-center justify-center rounded py-4 mr-2 min-h-[100px] bg-white dark:bg-dark-gray hover:bg-light-green dark:hover:bg-dark-green-900 focus:bg-light-green dark:focus:bg-dark-green-900 cursor-pointer transition-colors duration-300 ${isProviderSelected("generic_webhooks")
                        ? "border-2 border-status-green dark:border-dark-green-100"
                        : "border border-gray-lines dark:border-secondary-gray"
                        }`}
                    onClick={() => handleSelectProviderType("generic_webhooks")}
                    tabIndex={0}
                    role="button"
                    aria-label="Select Custom Webhook"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleSelectProviderType("generic_webhooks");
                        }
                    }}
                >
                    <img src={webhook} alt="webhook" className="h-[40px] mb-2" />
                    <label className='text-center text-base lg:text-lg text-off-black dark:text-white transition-colors duration-300 px-2'>Custom Webhook</label>
                </div>
                <div
                    className={`flex flex-col w-full sm:w-1/2 items-center justify-center rounded py-4 mr-2 min-h-[100px] bg-white dark:bg-dark-gray hover:bg-light-green dark:hover:bg-dark-green-900 focus:bg-light-green dark:focus:bg-dark-green-900 cursor-pointer transition-colors duration-300 ${isProviderSelected("api_push")
                        ? "border-2 border-status-green dark:border-dark-green-100"
                        : "border border-gray-lines dark:border-secondary-gray"
                        }`}
                    onClick={() => handleSelectProviderType("api_push")}
                    tabIndex={0}
                    role="button"
                    aria-label="Select API Push"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleSelectProviderType("api_push");
                        }
                    }}
                >
                    <img src={api} alt="api" className={`${isModal ? 'h-12' : 'h-[40px]'} mb-2`} />
                    <label className='text-center text-base lg:text-lg text-off-black dark:text-white transition-colors duration-300 px-2'>API Push</label>
                </div>
            </div>

            <p className="text-medium-gray dark:text-gray-lines transition-colors duration-300">Still can't find what you're looking for? Reach out to <a href="mailto:support@earthranger.com" className="text-status-green dark:text-dark-green-100 underline transition-colors duration-300">support@earthranger.com</a> to request new integrations.</p>
        </>
    );

    // If this is a modal and showModal is true, wrap content in modal container
    if (isModal && showModal) {
        return (
            <div className="fixed inset-0 z-50">
                <div
                    className="fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 transition-colors duration-300"
                    onClick={() => setShowModal && setShowModal(false)}
                ></div>
                <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="z-20 flex flex-col w-full sm:w-5/6 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow rounded-lg bg-white dark:bg-dark-gray transition-colors duration-300">
                        <div className="bg-white dark:bg-dark-gray z-30 sticky top-0 flex flex-col rounded-lg transition-colors duration-300">
                            <div className="flex flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={() => setShowModal && setShowModal(false)}
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-3 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-300"
                                    data-modal-hide="authentication-modal"
                                >
                                    <svg aria-hidden="true" className="w-7 h-7 text-off-black dark:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                    <span className="sr-only">{t("Close Modal")}</span>
                                </button>
                            </div>
                        </div>
                        <div className="mb-4 px-5 text-off-black dark:text-white transition-colors duration-300">
                            {renderContent()}
                        </div>
                        {/* Next and Cancel buttons */}
                        <div className="flex justify-end gap-4 px-5 py-4">
                            <button
                                onClick={() => setShowModal && setShowModal(false)}
                                className={buttonClasses.secondary}
                            >
                                {t("Cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return renderContent();
};

export default WebhookApiPushModal;
